import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Mail, KeyRound, Lock, Smartphone, LogOut } from "lucide-react";
import { api, LOGO_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

function fmt(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  return JSON.stringify(detail);
}

export default function AdminSettings() {
  const nav = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [creds, setCreds] = useState({ current_password: "", new_email: "", new_password: "" });
  const [savingCreds, setSavingCreds] = useState(false);
  // MFA
  const [setupData, setSetupData] = useState(null);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [disableForm, setDisableForm] = useState({ password: "", code: "" });

  useEffect(() => {
    if (user === false) nav("/admin/login");
  }, [user, nav]);

  useEffect(() => {
    if (user) setCreds((c) => ({ ...c, new_email: user.email }));
  }, [user?.email]);

  const saveCreds = async (e) => {
    e.preventDefault();
    if (!creds.current_password) return toast.error("Enter current password");
    const payload = { current_password: creds.current_password };
    if (creds.new_email && creds.new_email !== user.email) payload.new_email = creds.new_email;
    if (creds.new_password) payload.new_password = creds.new_password;
    if (!payload.new_email && !payload.new_password) return toast.error("Change email or password");
    setSavingCreds(true);
    try {
      const { data } = await api.patch("/auth/credentials", payload);
      toast.success(`Updated. Signed in as ${data.email}.`);
      await refresh();
      setCreds({ current_password: "", new_email: data.email, new_password: "" });
    } catch (err) {
      toast.error(fmt(err?.response?.data?.detail) || "Update failed");
    } finally {
      setSavingCreds(false);
    }
  };

  const startMfa = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSetupData(data);
    } catch (err) {
      toast.error(fmt(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const confirmMfa = async () => {
    if (otp.length !== 6) return toast.error("Enter 6-digit code");
    setBusy(true);
    try {
      await api.post("/auth/mfa/enable", { code: otp });
      toast.success("MFA enabled");
      setSetupData(null);
      setOtp("");
      await refresh();
    } catch (err) {
      toast.error(fmt(err?.response?.data?.detail) || "Bad code");
    } finally {
      setBusy(false);
    }
  };

  const disableMfa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", disableForm);
      toast.success("MFA disabled");
      setDisableForm({ password: "", code: "" });
      await refresh();
    } catch (err) {
      toast.error(fmt(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="border-b border-black/10 bg-white sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-sm text-[#6B7280] hover:text-[#111] inline-flex items-center gap-1"><ArrowLeft size={14}/> Console</Link>
            <img src={LOGO_URL} alt="B-Raw" className="h-8 w-auto ml-3" />
          </div>
          <Button variant="outline" onClick={async () => { await logout(); nav("/admin/login"); }} className="rounded-full border-black/10" data-testid="logout-btn"><LogOut size={14} className="mr-2" /> Logout</Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-[#111]">Account Settings</h1>
        <div className="text-sm text-[#6B7280] mt-1 flex flex-wrap items-center gap-2">
          <span>Signed in as <b>{user?.email}</b> · role <b>{user?.role}</b> · MFA</span>
          {user?.mfa_enabled
            ? <Badge data-testid="mfa-badge" className="bg-[#166534] hover:bg-[#166534]">enabled</Badge>
            : <Badge data-testid="mfa-badge" variant="secondary">disabled</Badge>}
        </div>

        {/* Credentials */}
        <section className="mt-8 bg-white border border-black/10 rounded-2xl p-6 sm:p-8">
          <div className="flex items-center gap-2">
            <KeyRound className="text-[#D9381E]" />
            <h2 className="font-display font-bold text-xl text-[#111]">Login credentials</h2>
          </div>
          <p className="text-sm text-[#6B7280] mt-1">Change your email or password. Requires your current password.</p>
          <form onSubmit={saveCreds} className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="creds-form">
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Current password</Label>
              <Input required type="password" value={creds.current_password} onChange={(e) => setCreds({ ...creds, current_password: e.target.value })} data-testid="creds-current" className="mt-1 h-12" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#6B7280]">New email</Label>
              <Input type="email" value={creds.new_email} onChange={(e) => setCreds({ ...creds, new_email: e.target.value })} data-testid="creds-email" className="mt-1 h-12" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#6B7280]">New password (min 8)</Label>
              <Input type="password" value={creds.new_password} onChange={(e) => setCreds({ ...creds, new_password: e.target.value })} data-testid="creds-newpass" className="mt-1 h-12" placeholder="Leave blank to keep" />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={savingCreds} data-testid="creds-save" className="bg-[#D9381E] hover:bg-[#B02C15] rounded-full">
                {savingCreds ? "Saving…" : "Update credentials"}
              </Button>
            </div>
          </form>
        </section>

        {/* MFA */}
        <section className="mt-8 bg-white border border-black/10 rounded-2xl p-6 sm:p-8">
          <div className="flex items-center gap-2">
            <ShieldCheck className="text-[#D9381E]" />
            <h2 className="font-display font-bold text-xl text-[#111]">Two-factor authentication (TOTP)</h2>
          </div>
          <p className="text-sm text-[#6B7280] mt-1">Adds a 6-digit code from an authenticator app (Google Authenticator, Authy, 1Password) on every sign-in.</p>

          {user?.mfa_enabled ? (
            <form onSubmit={disableMfa} className="mt-6 space-y-4" data-testid="mfa-disable-form">
              <div className="p-4 rounded-lg bg-[#DCFCE7] text-[#166534] text-sm font-semibold">MFA is currently ENABLED on this account.</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Password</Label>
                  <Input type="password" required value={disableForm.password} onChange={(e) => setDisableForm({ ...disableForm, password: e.target.value })} data-testid="mfa-disable-pass" className="mt-1 h-12" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Current 6-digit code</Label>
                  <Input required value={disableForm.code} onChange={(e) => setDisableForm({ ...disableForm, code: e.target.value.replace(/\D/g, "").slice(0,6) })} data-testid="mfa-disable-code" className="mt-1 h-12 tracking-widest" />
                </div>
              </div>
              <Button type="submit" disabled={busy} variant="outline" className="rounded-full border-black/10 text-[#D9381E] border-[#D9381E]" data-testid="mfa-disable-btn">Disable MFA</Button>
            </form>
          ) : setupData ? (
            <div className="mt-6 space-y-4" data-testid="mfa-setup">
              <div className="grid sm:grid-cols-2 gap-6 items-start">
                <div className="text-center bg-[#F3EAD3] rounded-2xl p-6">
                  <img src={setupData.qr_png_base64} alt="MFA QR" className="mx-auto" data-testid="mfa-qr" />
                  <div className="mt-2 text-xs text-[#6B7280]">Scan with your authenticator app</div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Or enter this secret manually</Label>
                  <div className="mt-1 p-3 rounded-lg bg-[#F3EAD3] font-mono text-sm break-all" data-testid="mfa-secret">{setupData.secret}</div>
                  <Label className="mt-4 text-xs uppercase tracking-widest text-[#6B7280]">6-digit code from app</Label>
                  <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0,6))} inputMode="numeric" className="mt-1 h-12 tracking-widest" data-testid="mfa-confirm-code" />
                  <Button onClick={confirmMfa} disabled={busy || otp.length !== 6} className="mt-4 bg-[#D9381E] hover:bg-[#B02C15] rounded-full" data-testid="mfa-confirm-btn">
                    {busy ? "Enabling…" : "Confirm & enable"}
                  </Button>
                  <button className="ml-2 text-sm text-[#6B7280]" onClick={() => { setSetupData(null); setOtp(""); }}>Cancel</button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6">
              <Button onClick={startMfa} disabled={busy} className="bg-[#D9381E] hover:bg-[#B02C15] rounded-full" data-testid="mfa-start"><Smartphone size={16} className="mr-2" /> Enable MFA</Button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
